import { useCallback, useState } from 'react';
import { AlertDialog } from '@Components/AlertDialog';
import { Button } from '@Components/Button';
import { Checkbox } from '@Components/FormCheck';
import { Appearance, Intent } from '@Components/designConstants';
import { useSetError } from '../../redux/modals';
import { ClientToolsLambdaFetch } from '../../utils/fetch-utils';
import { ALL_SYNC_TARGETS, SYNC_ENDPOINT_MAP, SyncTargetType } from './cps-utils';

const SalesforceResyncSection = () => {
  const setError = useSetError();
  const [selected, setSelected] = useState<Set<SyncTargetType>>(new Set());
  const [syncing, setSyncing] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const allSelected = selected.size === ALL_SYNC_TARGETS.length;

  const toggleTarget = useCallback((target: SyncTargetType) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(target)) {
        next.delete(target);
      } else {
        next.add(target);
      }
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    setSelected(prev =>
      prev.size === ALL_SYNC_TARGETS.length ? new Set() : new Set(ALL_SYNC_TARGETS)
    );
  }, []);

  const handleSync = useCallback(async () => {
    if (selected.size === 0) return;
    setSyncing(true);
    try {
      const results = await Promise.allSettled(
        [...selected].map(target =>
          ClientToolsLambdaFetch(SYNC_ENDPOINT_MAP[target], { method: 'POST' })
        )
      );
      const failed = results.filter(r => r.status === 'rejected');
      if (failed.length > 0) {
        setError({
          message: `${failed.length} sync(s) failed. Check logs for details.`,
          reportError: failed[0].status === 'rejected' ? failed[0].reason : undefined
        });
      } else {
        setSuccessMessage(`Successfully synced: ${[...selected].join(', ')}`);
        setSelected(new Set());
      }
    } catch (e) {
      setError({ message: `Sync failed: ${e.message}`, reportError: e });
    } finally {
      setSyncing(false);
    }
  }, [selected, setError]);

  return (
    <div className='cps-admin-section'>
      <h6>Salesforce Re-sync</h6>
      <div className='cps-admin-sync-options'>
        <Checkbox id='cps-sync-all' label='Select All' checked={allSelected} onChange={toggleAll} />
        {ALL_SYNC_TARGETS.map(target => (
          <Checkbox
            key={target}
            id={`cps-sync-${target}`}
            label={target}
            checked={selected.has(target)}
            onChange={() => toggleTarget(target)}
          />
        ))}
      </div>
      <Button
        appearance={Appearance.OUTLINED}
        onClick={() => handleSync()}
        disabled={selected.size === 0}
        loading={syncing}
      >
        Re-sync Salesforce Data
      </Button>

      <AlertDialog
        show={!!successMessage}
        onHide={() => setSuccessMessage('')}
        intent={Intent.SUCCESS}
        title='Sync Complete'
        body={successMessage}
        primaryAction={{ onClick: () => setSuccessMessage('') }}
      />
    </div>
  );
};

export { SalesforceResyncSection };
